import { TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Stock {
  symbol: string;
  price: string;
  change: string;
  up: boolean;
}

const fallbackStocks: Stock[] = [
  { symbol: "AAPL", price: "268.62", change: "+1.53%", up: true },
  { symbol: "NVDA", price: "191.01", change: "+0.63%", up: true },
  { symbol: "VOLV-B", price: "268.40", change: "+1.12%", up: true },
  { symbol: "ERIC-B", price: "78.50", change: "-0.38%", up: false },
  { symbol: "H&M", price: "172.30", change: "+0.65%", up: true },
  { symbol: "SEB-A", price: "142.80", change: "+1.45%", up: true },
  { symbol: "SAND", price: "218.60", change: "-0.72%", up: false },
  { symbol: "ATCO-A", price: "612.40", change: "+0.88%", up: true },
];

const StockTicker = () => {
  const [stocks, setStocks] = useState<Stock[]>(fallbackStocks);
  const [isLive, setIsLive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStockPrices = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-stock-prices');

      if (error) {
        // Non-critical: keep showing sample prices instead of surfacing an error.
        setIsLive(false);
        return;
      }

      if (data?.stocks && data.stocks.length > 0) {
        setStocks(data.stocks);
        setIsLive(true);
      }
    } catch {
      setIsLive(false);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    // Defer API call to not block LCP - wait for idle time or 2 seconds
    const timeoutId = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        (window as typeof window & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(fetchStockPrices);
      } else {
        fetchStockPrices();
      }
    }, 2000);
    
    const interval = setInterval(fetchStockPrices, 60000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="laser-glass overflow-hidden relative">
      {/* Subtle inner glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, hsl(210 50% 30% / 0.1) 50%, transparent 100%)',
        }}
      />
      
      <div className="flex animate-scroll">
        {/* Live indicator - Glowing inside glass */}
        <div className="flex items-center gap-2 px-5 py-4 border-r border-white/10 whitespace-nowrap bg-white/5 relative z-10">
          {isLive ? (
            <>
              <span className="w-2 h-2 rounded-full bg-emerald animate-glow-pulse glow-green" />
              <span className="text-xs text-white/70 font-semibold tracking-wider">LIVE</span>
            </>
          ) : (
            <>
              <RefreshCw className={`w-3 h-3 text-white/60 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="text-xs text-white/60 font-medium tracking-wider">SAMPLE</span>
            </>
          )}
        </div>
        
        {/* Stock items with glowing numbers */}
        {[...stocks, ...stocks].map((stock, index) => (
          <div
            key={index}
            className="flex items-center gap-3 px-6 py-4 border-r border-white/5 whitespace-nowrap group transition-all duration-300 hover:bg-white/5"
          >
            <span className="font-bold text-white text-sm tracking-tight">
              {stock.symbol}
            </span>
            <span className="text-white/50 text-sm font-medium tabular-nums">
              {['AAPL', 'NVDA'].includes(stock.symbol) ? '$' : ''}{stock.price}{['AAPL', 'NVDA'].includes(stock.symbol) ? '' : ' kr'}
            </span>
            <span
              className={`
                flex items-center gap-1 text-sm font-bold tabular-nums
                ${stock.up ? "glow-green" : "glow-red"}
                transition-all duration-300
                group-hover:scale-110
              `}
            >
              {stock.up ? (
                <TrendingUp className="w-3.5 h-3.5" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5" />
              )}
              {stock.change}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StockTicker;